import { useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  IconArrowUpRight,
  IconBook2,
  IconFileText,
  IconPhone,
  IconPlus,
  IconRobot,
  IconRoute,
  IconSearch,
} from '@tabler/icons-react';
import { formatCampaignStatusLabel } from '@pages/Admin/Main/Campaign/ui/CampaignStatusButton/utils';
import { useAppSelector } from '@shared/store';
import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';
import {
  useCampaignListPaginatedControllerGetCampaignListPaginatedQuery,
  useCampaignAgentListControllerListQuery,
  useCampaignControllerCampaignQuery,
  type CampaignListItemDto,
} from '@shared/services/apiService';
import { CampaignPicker } from './CampaignPicker';
import { workspaceAgentPath } from './workspacePaths';
import { CreateAssistantDialog } from './CreateAssistantDialog';

const CampaignAgentRows = ({
  campaign,
  search,
  agentType,
}: {
  campaign: CampaignListItemDto;
  search: string;
  agentType: string;
}) => {
  const { variant } = useDesignVariant();
  const workspace = useDesignWorkspace();
  const detail = useCampaignControllerCampaignQuery({ id: campaign.id });
  const direct = useCampaignAgentListControllerListQuery({ id: campaign.id });
  const notice = (children: ReactNode) =>
    variant === 'v4' ? (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {children}
      </Paper>
    ) : (
      <TableRow>
        <TableCell colSpan={variant === 'v3' ? 6 : 5}>{children}</TableCell>
      </TableRow>
    );
  if (detail.isError || direct.isError)
    return notice(
      <Alert
        severity="error"
        action={
          <Button
            onClick={() => {
              void detail.refetch();
              void direct.refetch();
            }}>
            Retry
          </Button>
        }>
        Unable to load agents for {campaign.title}.
      </Alert>,
    );
  if (!detail.currentData || !direct.currentData)
    return notice(<CircularProgress size={18} aria-label={`Loading ${campaign.title} agents`} />);
  const agents = detail.currentData.squad?.agents ?? direct.currentData;
  const rows = agents.filter(
    (agent) =>
      `${agent.name} ${agent.profile?.name || ''} ${campaign.title}`
        .toLocaleLowerCase()
        .includes(search) &&
      (agentType === 'all' ||
        (agent.profile?.taskFlow?.tasks.length ? 'workflow' : 'prompt') === agentType),
  );
  const open = (agentId: string) => workspace.navigate(workspaceAgentPath(campaign.id, agentId));
  if (variant === 'v4')
    return (
      <>
        {rows.length
          ? rows.map((agent) => (
              <Paper
                key={agent.id}
                variant="outlined"
                sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Stack gap={2} sx={{ p: 2.5, flex: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: 'surface.raised',
                        color: 'text.primary',
                        borderRadius: '50%',
                      }}>
                      <IconRoute size={21} stroke={1.5} />
                    </Avatar>
                    <Chip
                      label={campaign.type === 'inbound' ? 'Inbound' : 'Outbound'}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="body1" fontWeight={600}>
                    {agent.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: 42,
                    }}>
                    {agent.profile?.prompt ||
                      agent.profile?.name ||
                      'Instructions configured in this campaign.'}
                  </Typography>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {agent.voice?.name || 'Voice configured in campaign'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.5 }}>
                      {campaign.title}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatCampaignStatusLabel(campaign.status)}
                  </Typography>
                  <Button
                    onClick={() => open(agent.id)}
                    endIcon={<IconArrowUpRight size={14} />}
                    aria-label={`Open ${agent.name}`}>
                    Open
                  </Button>
                </Stack>
              </Paper>
            ))
          : notice(
              <Typography variant="body2" color="text.secondary">
                No matching agents in {campaign.title}.
              </Typography>,
            )}
      </>
    );
  return rows.length ? (
    <>
      {rows.map((agent) => (
        <TableRow key={agent.id} hover>
          <TableCell>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Avatar
                variant="rounded"
                src={agent.avatarUrl || undefined}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  fontSize: 13,
                }}>
                {agent.name.slice(0, 1)}
              </Avatar>
              <Box>
                <Button
                  onClick={() => open(agent.id)}
                  sx={{ px: 0, minHeight: 24, color: 'text.primary', fontWeight: 600 }}>
                  {agent.name}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  {agent.voice?.name || 'Voice configured in campaign'}
                </Typography>
              </Box>
            </Stack>
          </TableCell>
          <TableCell>
            {variant === 'v3' ? (
              <Chip
                label={agent.profile?.taskFlow?.tasks.length ? 'Workflow' : 'Prompt'}
                variant="outlined"
              />
            ) : (
              agent.profile?.name || 'Campaign instructions'
            )}
          </TableCell>
          <TableCell>{variant === 'v3' ? agent.voice?.name || '—' : campaign.title}</TableCell>
          <TableCell>
            {['v3', 'v4'].includes(variant) ? (
              campaign.title
            ) : (
              <Chip
                label={agent.profile?.taskFlow?.tasks.length ? 'Workflow' : 'Prompt'}
                variant="outlined"
              />
            )}
          </TableCell>
          {variant === 'v3' && (
            <TableCell>
              <Chip
                label={formatCampaignStatusLabel(campaign.status)}
                size="small"
                variant="outlined"
              />
            </TableCell>
          )}
          <TableCell>
            <Button
              aria-label={`Open ${agent.name}`}
              onClick={() => open(agent.id)}
              sx={{ minWidth: 30 }}>
              <IconArrowUpRight size={16} />
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </>
  ) : (
    <TableRow>
      <TableCell colSpan={variant === 'v3' ? 6 : 5} sx={{ color: 'text.secondary' }}>
        {search || agentType !== 'all'
          ? `No matching agents in ${campaign.title}`
          : `No agents in ${campaign.title}`}{' '}
        ·{' '}
        <Button
          onClick={() =>
            workspace.navigate(`/admin/campaign/${encodeURIComponent(campaign.id)}/edit?tab=agents`)
          }>
          Open setup
        </Button>
      </TableCell>
    </TableRow>
  );
};

/** Campaign agents, not reusable instruction profiles. Uses the existing scoped API only. */
export const AgentDirectoryWorkspace = () => {
  const { variant } = useDesignVariant();
  const workspace = useDesignWorkspace();
  const viewer = useAppSelector((state) => state.auth.authenticatedUser?.role === 'viewer');
  const [query, setQuery] = useState('');
  const [agentType, setAgentType] = useState('all');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<CampaignListItemDto | null>(null);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const list = useCampaignListPaginatedControllerGetCampaignListPaginatedQuery(
    { limit: 20, cursor: cursors.at(-1), sortBy: 'title', sortOrder: 'ASC', withTotal: true },
    { skip: !!selected },
  );
  const campaigns = selected ? [selected] : list.currentData?.data || [];
  const create = (
    <Button
      variant={variant === 'v4' ? 'outlined' : 'contained'}
      startIcon={variant === 'v4' ? undefined : <IconPlus size={16} />}
      disabled={viewer}
      onClick={() => setCreating(true)}>
      {variant === 'v5'
        ? 'New assistant'
        : variant === 'v4'
          ? 'Start from scratch'
          : 'Create an agent'}
    </Button>
  );
  const creationDialog = (
    <CreateAssistantDialog open={creating} onClose={() => setCreating(false)} />
  );
  if (list.isError && !selected)
    return (
      <Alert severity="error" action={<Button onClick={() => void list.refetch()}>Retry</Button>}>
        Unable to load current campaigns.
      </Alert>
    );
  if (list.isFetching && !list.currentData && !selected)
    return <CircularProgress aria-label="Loading agents" />;
  if (variant === 'v4' && !campaigns.length)
    return (
      <Stack gap={4} sx={{ maxWidth: 540, mx: 'auto', py: { xs: 3, md: 7 } }}>
        <Box>
          <IconRobot size={28} stroke={1.5} />
          <Typography component="h1" variant="h3" sx={{ mt: 3 }}>
            Agents
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2, lineHeight: 1.8 }}>
            Build voice agents that follow your playbook. Give them a voice, connect your resources,
            and bring every conversation into one workspace.
          </Typography>
        </Box>
        {[
          {
            icon: IconPhone,
            title: 'Voice on the phone',
            body: 'Prepare agents for inbound and outbound conversations.',
          },
          {
            icon: IconRoute,
            title: 'Your conversation, step by step',
            body: 'Start with instructions and configure the conversation.',
          },
          {
            icon: IconBook2,
            title: 'Grounded in your business',
            body: 'Use campaign knowledge, tools and follow-up rules.',
          },
        ].map(({ icon: Icon, title, body }) => (
          <Stack
            key={title}
            direction="row"
            gap={2}
            sx={{ pb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Icon size={21} stroke={1.5} />
            <Box>
              <Typography fontWeight={550}>{title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {body}
              </Typography>
            </Box>
          </Stack>
        ))}
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            disabled={viewer}
            startIcon={<IconPlus size={16} />}
            onClick={() => workspace.open('Jobs')}>
            Create from template
          </Button>
          {create}
        </Stack>
        {creationDialog}
      </Stack>
    );
  return (
    <Stack gap={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="start" gap={2}>
        <Box>
          <Typography component="h1" variant="h3">
            {variant === 'v5' ? 'Assistants' : variant === 'v4' ? 'Agents' : 'All agents'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {variant === 'v5'
              ? 'Your voices, instructions and campaign context, together.'
              : 'Build, configure and test the voices behind your campaigns.'}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {variant === 'v4' && (
            <Button
              variant="contained"
              disabled={viewer}
              startIcon={<IconPlus size={16} />}
              onClick={() => workspace.open('Jobs')}>
              Create from template
            </Button>
          )}
          {create}
        </Stack>
      </Stack>
      {variant === 'v5' && (
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            startIcon={<IconFileText size={15} />}
            onClick={() => workspace.open('Jobs')}>
            Job library
          </Button>
          <Button onClick={() => workspace.open('Knowledge base')}>Knowledge</Button>
          <Button onClick={() => workspace.open('Tools')}>Agent tools</Button>
        </Stack>
      )}
      <Paper
        variant="outlined"
        sx={
          variant === 'v4'
            ? { border: 0, bgcolor: 'transparent', overflow: 'visible' }
            : { overflow: 'hidden' }
        }>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1.5}
          sx={{ p: variant === 'v4' ? 0 : 2, mb: variant === 'v4' ? 3 : 0 }}>
          <TextField
            size="small"
            placeholder="Search agents, jobs or campaigns"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ 'aria-label': 'Search campaign agents' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch size={16} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { md: 320 } }}
          />
          <TextField
            select
            size="small"
            label="Agent type"
            value={agentType}
            onChange={(event) => setAgentType(event.target.value)}
            sx={{ minWidth: 160 }}>
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="prompt">Prompt</MenuItem>
            <MenuItem value="workflow">Workflow</MenuItem>
          </TextField>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CampaignPicker value={selected} onChange={setSelected} compact />
          </Box>
        </Stack>
        {variant === 'v4' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
            }}>
            {campaigns.map((campaign) => (
              <CampaignAgentRows
                key={campaign.id}
                campaign={campaign}
                search={query.trim().toLocaleLowerCase()}
                agentType={agentType}
              />
            ))}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table aria-label="Campaign agents" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    {(variant === 'v3'
                      ? ['Agent name', 'Type', 'Voice', 'Campaign', 'Status', '']
                      : ['Assistant', 'Job', 'Campaign', 'Type', '']
                    ).map((label, index) => (
                      <TableCell key={index}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <CampaignAgentRows
                      key={campaign.id}
                      campaign={campaign}
                      search={query.trim().toLocaleLowerCase()}
                      agentType={agentType}
                    />
                  ))}
                  {!campaigns.length && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 7 }}>
                        <IconRobot size={30} stroke={1.5} />
                        <Typography variant="h6" sx={{ mt: 2 }}>
                          Build your first agent
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Start with a job, choose a voice and save an agent in a campaign.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {!selected && (
          <Stack direction="row" gap={1} justifyContent="end" alignItems="center" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Campaign page {cursors.length} · Search applies to this page
            </Typography>
            <Button
              disabled={cursors.length === 1 || list.isFetching}
              onClick={() => setCursors((previous) => previous.slice(0, -1))}>
              Previous
            </Button>
            <Button
              disabled={!list.currentData?.meta.hasMore || list.isFetching}
              onClick={() =>
                setCursors((previous) => [...previous, list.currentData!.meta.cursor!])
              }>
              Next campaigns
            </Button>
          </Stack>
        )}
      </Paper>
      {creationDialog}
    </Stack>
  );
};
